import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {User} from '../../classes/user.class';
import {UserService} from '../../services/user/user.service';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatInput, MatPrefix} from '@angular/material/input';
import {CodaltComponent} from '../../codalt.component';
import {DatePipe} from '@angular/common';
import {Utils} from '../../utils.class';
import {AvatarComponent} from '../../shared/ui/avatar/avatar.component';
import {DataListComponent} from '../../shared/ui/data-list/data-list.component';
import {DataRowComponent} from '../../shared/ui/data-list/data-row.component';
import {EmptyStateComponent} from '../../shared/ui/empty-state/empty-state.component';
import {FilterChipsComponent, FilterOption} from '../../shared/ui/filter-chips/filter-chips.component';
import {PageHeaderComponent} from '../../shared/ui/page-header/page-header.component';
import {PageComponent} from '../../shared/ui/page/page.component';
import {StatusBadgeComponent} from '../../shared/ui/status-badge/status-badge.component';

/** Display group of a user in the overview chips; filtering is for display only. */
type UserGroup = 'alle' | 'actief' | 'geenrol' | 'uit';

@Component({
    selector: 'app-users-overview',
    templateUrl: './users-overview.component.html',
    styleUrls: ['./users-overview.component.scss'],
    imports: [
        MatButton,
        MatFormField,
        MatInput,
        MatPrefix,
        RouterLink,
        DatePipe,
        AvatarComponent,
        DataListComponent,
        DataRowComponent,
        EmptyStateComponent,
        FilterChipsComponent,
        PageComponent,
        PageHeaderComponent,
        StatusBadgeComponent,
    ]
})
export class UsersOverviewComponent extends CodaltComponent implements OnInit, OnDestroy {

    readonly users = signal<User[]>(null);
    readonly query = signal('');
    readonly group = signal<UserGroup>('actief');

    /** Users matching the search, with the values the rows show. */
    private readonly searched = computed(() => {
        const query = this.query().trim().toLowerCase();
        const today = Utils.formatDate(new Date());
        return (this.users() ?? [])
            .filter(user => !query || `${user.name} ${user.email}`.toLowerCase().includes(query))
            .map(user => {
                const out = !!user.employment_end && Utils.formatDate(user.employment_end) < today;
                const group: UserGroup = out ? 'uit' : user.roles?.length ? 'actief' : 'geenrol';
                return {user, group, out};
            })
            // Users in service first, then by start date (unknown start date last)
            .sort((a, b) => Number(a.out) - Number(b.out) || startTime(a.user) - startTime(b.user));
    });

    readonly rows = computed(() => this.searched().filter(row => this.group() === 'alle' || row.group === this.group()));

    readonly groups = computed<FilterOption<UserGroup>[]>(() => {
        const rows = this.searched();
        const count = (group: UserGroup) => group === 'alle' ? rows.length : rows.filter(row => row.group === group).length;
        return [
            {value: 'alle', label: 'Alle', color: '#9aa0a6', count: count('alle')},
            {value: 'actief', label: 'Actief', color: '#3fb97a', count: count('actief')},
            {value: 'geenrol', label: 'Zonder rol', color: '#e0a100', count: count('geenrol')},
            {value: 'uit', label: 'Uit dienst', color: '#c9ccd1', count: count('uit')},
        ];
    });

    constructor(
        private userService: UserService,
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.userService.getList().subscribe(users => {
                const sortedUsers = users.sort((a, b) =>
                    new Date(a.employment_end).getTime() - new Date(b.employment_end).getTime() ||
                    new Date(a.employment_start).getTime() - new Date(b.employment_start).getTime()
                );
                this.users.set(sortedUsers)
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}

function startTime(user: User): number {
    return user.employment_start ? new Date(user.employment_start).getTime() : Number.MAX_SAFE_INTEGER;
}
